import { useContext, useEffect, useMemo, useState } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import TopBar from "./TopBar";
import { useNavItems, pageTitleFor } from "./navItems";
import BottomNav from "./bottomNav";
import Home from "../Home/Index";
import Team from "../Team/Index";
import Schedule from "../Schedule";
import Analytics from "../Analytics/Index";
import Settings from "../Settings"
import { Box } from "../../Components/Boxes";
import Icon from "../../Components/Icons";
import StaffProfile from "../Team/Staff/Profile/Index";
import ClientProfile from "../Team/Clients/Profile/Index";
import { useResponsive } from "../../Components/Hooks/index";
import { AuthContext } from "../../contextAPI's/authContext";
import { Text } from "../../Components/Text";
import { DropDownMenu, DropDownMenuItem } from "../../Components/Modals";
import { useCookieWatcher } from "@fcannizzaro/react-use-cookie-watcher";
import useStaffDispathers from "../../redux/dispatchers/useStaffDispatchers";
import useConfigsDispatchers from "../../redux/dispatchers/useConfigsDispatchers";
import useBusinessAccountDispatchers from "../../redux/dispatchers/useBusinessAccountDispatchers";
import { localStorageService } from "../../utiles/storage";
import { useBusinessAccountSelector, useBusinessAccountsSelector } from "../../redux/selectors/useBusinessAccountSelectors";
import usePermissionsController from "../../permissions";
import { PermissionDeniedScreen } from "../../Components/Permissions";
import TimeSheetsPage from "../Timesheets";
import Subscriptions from "../Subscriptions";
import CareRecords from "../CareRecords";
import { CARE_RECORDS_ENABLED } from "../../constants/careRecords";
import { SUBSCRIPTION_STATUS } from "../../constants";
import { ACCESS_STATE, getAccessState } from "../../accessState";
import { TRIAL_COPY } from "../../copy/trialCopy";
import AccessBanner from "../../Components/AccessBanner";
import LockScreen from "./LockScreen";

export default function Dashboard() {
  //auth context
  const { Logout, loading } = useContext(AuthContext);
  const { getGlobalConfigs } = useConfigsDispatchers()
  const { clearSelectedBusinessAccount, getBusinessAccounts } = useBusinessAccountDispatchers()
  const {
    accessWeb,
    accessHomePage,
    accessAnalyticsPage,
    accessTeamsPage,
    accessSchedulePage,
    accessSettingsPage,
    accessTimeOffPage,
    accessTimesheetsPage,
    accessSubscriptionPage,

    accessStaffList,
    accessClientList,

    subscriptionsEnabled,
  } = usePermissionsController()

  // TODO: Make our own custom cookie watcher
  const navigate = useNavigate();
  const location = useLocation();
  // location.pathname;

  //checking whether the accessToken is expired or not
  const isNotExpired = useCookieWatcher("accessToken", {
    checkEvery: 500,
  });

  useEffect(() => {
    if (!isNotExpired) Logout();
  }, [isNotExpired]);

  useEffect(() => {
    getGlobalConfigs()
  }, [])

  // ---------------- selector ----------------------------
  const businessAccounts = useBusinessAccountsSelector()
  const businessAccount = useBusinessAccountSelector()

  // ---------------- all states ----------------------------
  // null = follow the breakpoint (expanded from 1280px), otherwise the user's choice.
  const [userCollapse, setUserCollapse] = useState(null);
  const isWide = useResponsive({ xs: false, lg: true });
  const sideBarCollapse = userCollapse ?? !isWide;
  const navItems = useNavItems();
  const [selected, setSelected] = useMemo(() => [location.pathname, () => { }], [location.pathname]);
  const isDesktop = useResponsive({ xs: false, md: true });

  // states for the drop down profile menu item
  const [showDropDown, setShowDropDown] = useState(false);
  const [dropDownAnchor, setDropDownAnchor] = useState(null);

  // ------------------- functions --------------------------

  const onProfileClick = async () => {
    navigate(`/team/staff/${localStorageService.getItem("staffId")}`)
  };

  const switchAccounts = async () => {
    getBusinessAccounts()
    localStorageService.deleteItem("businessAccountId")
    clearSelectedBusinessAccount()
  }

  function handleSideBar() {
    setUserCollapse(!sideBarCollapse);
  }
  function handleSelect(value) {
    // setSelected(value);
  }

  if (!accessWeb) {
    return (
      <PermissionDeniedScreen
        title={"You do not have access to the website"}
        subtitle={"Please download the app to continue"}
        logout={() => {
          Logout()
        }}
      />
    )
  }

  const subscriptionsApply = !!businessAccount && subscriptionsEnabled && businessAccount?.subscriptionDetails?.subscriptionsEnabled !== false
  const accessState = subscriptionsApply ? getAccessState(businessAccount) : ACCESS_STATE.DISABLED

  if (accessState === ACCESS_STATE.LOCKED || accessState === ACCESS_STATE.PAYMENT_PROBLEM) {
    // trial-backed lock: lock screen (owner sees the pricing table, others are told to contact the owner)
    if (accessState === ACCESS_STATE.LOCKED && businessAccount?.trial) {
      return (
        <LockScreen
          isOwner={!!accessSubscriptionPage}
          trial={businessAccount.trial}
          cancelled={businessAccount?.subscriptionDetails?.stripeSubscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED}
          logout={() => { Logout() }}
        />
      )
    }

    // legacy accounts and payment problems: unchanged behaviour
    if (!accessSubscriptionPage) {
      if (businessAccount?.subscriptionDetails?.stripeSubscriptionStatus == SUBSCRIPTION_STATUS.CANCELLED) {
        return (
          <PermissionDeniedScreen
            title={TRIAL_COPY.cancelledTitle}
            subtitle={TRIAL_COPY.contactManager}
            logout={() => {
              Logout()
            }}
          />
        )
      }

      return (
        <PermissionDeniedScreen
          title={TRIAL_COPY.problemTitle}
          subtitle={TRIAL_COPY.contactManager}
          logout={() => {
            Logout()
          }}
        />
      )
    } else {
      if (location.pathname !== "/subscription")
        navigate("/subscription")
    }
  }

  return (
    <Box
      $width="100%"
      $minHeight="100vh"
      $backgroundColor="bg-color"
      $position="relative"
    >
      <div style={{ display: "flex", minHeight: "100vh" }}>
        {isDesktop && (
          <SideBar
            selected={selected}
            handleSelect={handleSelect}
            sideBarCollapse={sideBarCollapse}
            handleSideBar={handleSideBar}
          />
        )}

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <AccessBanner
            accessState={accessState}
            trial={businessAccount?.trial}
            canChoosePlan={!!accessSubscriptionPage}
            onChoosePlan={() => navigate("/subscription")}
          />
          <TopBar
            title={pageTitleFor(location.pathname, navItems)}
            businessName={businessAccount?.businessName}
            canSwitch={!!businessAccounts && businessAccounts.length > 1}
            onSwitch={switchAccounts}
            loading={loading}
            isMobile={!isDesktop}
            onMenuClick={(e) => {
              setDropDownAnchor(e.currentTarget);
              setShowDropDown(true);
            }}
          />
          <main
            style={{
              flex: 1,
              minWidth: 0,
              padding: isDesktop ? "24px" : "16px 12px",
              paddingBottom: isDesktop ? "24px" : "88px",
            }}
          >
            <Routes>
              {(accessTimeOffPage || accessTeamsPage) && <Route path="/team" element={<Team />} />}
              {accessTeamsPage && accessClientList && <Route path="/team/clients" element={<Team defaultTab="Clients" />} />}
              {accessTeamsPage && accessStaffList && <Route path="/team/staff" element={<Team defaultTab="Staff" />} />}
              {accessTimeOffPage && <Route path="/team/timeoff" element={<Team defaultTab="Time Off" />} />}
              {CARE_RECORDS_ENABLED && accessClientList && <Route path="/care-records" element={<CareRecords />} />}
              {CARE_RECORDS_ENABLED && accessClientList && <Route path="/care-records/:module" element={<CareRecords />} />}
              {accessSchedulePage && <Route path="/schedule" element={<Schedule />} />}
              {accessAnalyticsPage && <Route path="/analytics" element={<Analytics />} />}
              <Route path="/team/staff/:staffid" element={<StaffProfile />} />
              <Route path="/team/clients/:clientId" element={<ClientProfile />} />
              {accessSettingsPage && <Route path="/settings" element={<Settings />} />}
              {accessHomePage && <Route path="/" element={<Home />} />}
              {accessTimesheetsPage && <Route path="/timesheets" element={<TimeSheetsPage />} />}
              {accessSubscriptionPage && <Route path="/subscription" element={<Subscriptions />} />}
              <Route path="*" element={<Navigate to={`/team/staff/${localStorageService.getItem("staffId")}`} />} />
            </Routes>
          </main>
        </div>
      </div>

      {!isDesktop && <BottomNav selected={selected} handleSelect={handleSelect} />}

      {showDropDown && (
        <DropDownMenu
          $onClose={() => {
            setDropDownAnchor(null);
            setShowDropDown(false);
          }}
          $open={showDropDown}
          $anchor={dropDownAnchor}
        >
          <DropDownMenuItem
            $onClick={() => {
              onProfileClick();
            }}
          >
            <Icon $icon="PersonRounded" $size="large" $color="primary" />
            <Text $fontSize={"body"} $marginLeft="10px" $color="primary">
              My Account
            </Text>
          </DropDownMenuItem>

          {businessAccounts && businessAccounts.length > 1 && <DropDownMenuItem
            $onClick={() => {
              switchAccounts();
            }}
          >
            <Icon $icon="Sync" $size="large" $color="primary" />
            <Text $fontSize={"body"} $marginLeft="10px" $color="primary">
              Switch Accounts
            </Text>
          </DropDownMenuItem>}

          <DropDownMenuItem
            $onClick={() => {
              Logout();
            }}
          >
            <Icon $icon="Logout" $size="large" $color="primary" />
            <Text $fontSize={"body"} $marginLeft="10px" $color="primary">
              Logout
            </Text>
          </DropDownMenuItem>
        </DropDownMenu>
      )}
    </Box>
  );
}
